import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Observable } from 'rxjs';

import { ModalComponent } from '../modal/modal';
import { placeRefusal } from '../modal/modal-refusal';
import { GENERIC_ERROR } from '../../core/http/read-error';

export interface ReleaseHoldDialogData {
  /** The page's own write (Kickstart or Debtors Finance); it re-reads its record. */
  readonly save: (releaseNote: string) => Observable<unknown>;
}

/** Release a hold, with the reason on the record. Closes only once the release lands. */
@Component({
  selector: 'app-release-hold-dialog',
  imports: [ModalComponent, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
  template: `
    <app-modal
      heading="Release this hold"
      submitLabel="Release hold"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      <mat-form-field appearance="outline" class="release-hold__field">
        <mat-label>Why is the hold being released?</mat-label>
        <textarea
          matInput
          formControlName="release_note"
          rows="3"
          maxlength="2000"
          data-testid="release-note"
        ></textarea>
        @if (form.controls.release_note.hasError('serverError')) {
          <mat-error>{{ form.controls.release_note.getError('serverError') }}</mat-error>
        } @else {
          <mat-error>A release must record why the hold was released.</mat-error>
        }
      </mat-form-field>
    </app-modal>
  `,
  styles: `
    .release-hold__field {
      width: 100%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseHoldDialogComponent {
  private readonly data = inject<ReleaseHoldDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<ReleaseHoldDialogComponent, boolean>>(MatDialogRef);

  protected readonly form = inject(FormBuilder).nonNullable.group({
    release_note: ['', [Validators.required, Validators.pattern(/\S/)]],
  });
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.pending.set(true);
    this.error.set(null);
    this.data.save(this.form.getRawValue().release_note.trim()).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err: unknown) => {
        this.pending.set(false);
        this.error.set(placeRefusal(this.form, err, GENERIC_ERROR));
      },
    });
  }
}
